import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Calculator, Ship, Clock, DollarSign, MapPin, ArrowRight, Loader2, AlertCircle, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { GlassPanel } from '@/components/layout';
import { cn } from '@/lib/cn';
import { staggerContainer, staggerItem } from '@/lib/motion';
import { calculatorApi, CalculationResponse, CalculationStep, RateType } from '@/lib/api';

type CalculatorType = 'distance' | 'laytime' | 'demurrage' | 'freight';

interface CalculatorOption {
  id: CalculatorType;
  name: string;
  description: string;
  icon: React.ElementType;
  color: string;
}

const calculators: CalculatorOption[] = [
  {
    id: 'distance',
    name: 'Distance & ETA',
    description: 'Calculate sea distance and estimated arrival time',
    icon: MapPin,
    color: 'cyan',
  },
  {
    id: 'laytime',
    name: 'Laytime',
    description: 'Calculate allowed time for loading/unloading',
    icon: Clock,
    color: 'purple',
  },
  {
    id: 'demurrage',
    name: 'Demurrage',
    description: 'Calculate penalties for exceeding laytime',
    icon: DollarSign,
    color: 'pink',
  },
  {
    id: 'freight',
    name: 'Freight Rate',
    description: 'Estimate freight costs and commissions',
    icon: Ship,
    color: 'blue',
  },
];

interface CalculationResultDisplay {
  response: CalculationResponse;
  formattedResult: string;
}

/**
 * Calculator Tab - Shipping calculation tools
 *
 * Features:
 * - Distance & ETA calculator with port database
 * - Laytime calculator with rate type support
 * - Demurrage/despatch calculator
 * - Freight & commission calculator
 * - Full audit trail for all calculations
 */
export function CalculatorTab() {
  const [activeCalculator, setActiveCalculator] = useState<CalculatorType>('distance');
  const [result, setResult] = useState<CalculationResultDisplay | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResult = useCallback((response: CalculationResponse, formattedResult: string) => {
    setResult({ response, formattedResult });
    setError(null);
  }, []);

  const handleError = useCallback((err: string) => {
    setError(err);
    setResult(null);
  }, []);

  const handleLoading = useCallback((isLoading: boolean) => {
    setLoading(isLoading);
    if (isLoading) {
      setError(null);
    }
  }, []);

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Calculator Selection */}
      <motion.div variants={staggerItem}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {calculators.map((calc) => {
            const Icon = calc.icon;
            const isActive = activeCalculator === calc.id;

            return (
              <button
                key={calc.id}
                onClick={() => {
                  setActiveCalculator(calc.id);
                  setResult(null);
                  setError(null);
                }}
                className={cn(
                  'p-4 rounded-xl border transition-all text-left',
                  isActive
                    ? `bg-ocean-${calc.color}/20 border-ocean-${calc.color}/40`
                    : 'bg-white/5 border-white/10 hover:border-white/20'
                )}
              >
                <Icon className={cn(
                  'w-6 h-6 mb-2',
                  isActive ? `text-ocean-${calc.color}` : 'text-white/50'
                )} />
                <p className={cn(
                  'font-medium text-sm',
                  isActive ? 'text-white' : 'text-white/70'
                )}>
                  {calc.name}
                </p>
                <p className="text-xs text-white/40 mt-1 line-clamp-1">
                  {calc.description}
                </p>
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* Calculator Forms */}
      <motion.div variants={staggerItem}>
        <GlassPanel className="p-6">
          {activeCalculator === 'distance' && (
            <DistanceCalculator
              onResult={handleResult}
              onError={handleError}
              onLoading={handleLoading}
            />
          )}
          {activeCalculator === 'laytime' && (
            <LaytimeCalculator
              onResult={handleResult}
              onError={handleError}
              onLoading={handleLoading}
            />
          )}
          {activeCalculator === 'demurrage' && (
            <DemurrageCalculator
              onResult={handleResult}
              onError={handleError}
              onLoading={handleLoading}
            />
          )}
          {activeCalculator === 'freight' && (
            <FreightCalculator
              onResult={handleResult}
              onError={handleError}
              onLoading={handleLoading}
            />
          )}
        </GlassPanel>
      </motion.div>

      {/* Loading State */}
      {loading && (
        <motion.div
          variants={staggerItem}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <GlassPanel className="p-6 border-ocean-cyan/30">
            <div className="flex items-center gap-3 text-ocean-cyan">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Calculating...</span>
            </div>
          </GlassPanel>
        </motion.div>
      )}

      {/* Error State */}
      {error && !loading && (
        <motion.div
          variants={staggerItem}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <GlassPanel className="p-6 border-signal-error/30 bg-signal-error/5">
            <div className="flex items-center gap-2 text-signal-error mb-2">
              <AlertCircle className="w-5 h-5" />
              <h3 className="font-medium">Calculation Error</h3>
            </div>
            <p className="text-sm text-white/70">{error}</p>
          </GlassPanel>
        </motion.div>
      )}

      {/* Result */}
      {result && !loading && (
        <motion.div
          variants={staggerItem}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <GlassPanel className="p-6 border-ocean-cyan/30">
            <div className="flex items-center gap-2 text-ocean-cyan mb-4">
              <Calculator className="w-5 h-5" />
              <h3 className="font-medium">Calculation Result</h3>
            </div>
            <pre className="whitespace-pre-wrap text-sm text-white/80 font-sans mb-4">
              {result.formattedResult}
            </pre>

            {/* Calculation Steps (Audit Trail) */}
            <CalculationSteps steps={result.response.steps} formula={result.response.formula} />

            {/* Warnings */}
            {result.response.warnings && result.response.warnings.length > 0 && (
              <div className="mt-4 p-3 rounded-lg bg-signal-warning/10 border border-signal-warning/30">
                <div className="flex items-center gap-2 text-signal-warning text-sm font-medium mb-2">
                  <AlertCircle className="w-4 h-4" />
                  Warnings
                </div>
                <ul className="space-y-1">
                  {result.response.warnings.map((warning, i) => (
                    <li key={i} className="text-xs text-white/60">• {warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </GlassPanel>
        </motion.div>
      )}
    </motion.div>
  );
}

// Calculation Steps Component (Audit Trail)
function CalculationSteps({ steps, formula }: { steps: CalculationStep[]; formula: string }) {
  const [expanded, setExpanded] = useState(false);

  if (!steps || steps.length === 0) return null;

  return (
    <div className="border-t border-white/10 pt-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm text-white/60 hover:text-white/80 transition-colors"
      >
        <Info className="w-4 h-4" />
        <span>Calculation Steps</span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          <div className="text-xs text-ocean-cyan/80 bg-ocean-cyan/10 p-2 rounded">
            Formula: {formula}
          </div>
          {steps.map((step, i) => (
            <div key={i} className="text-xs bg-white/5 p-3 rounded-lg">
              <div className="font-medium text-white/80 mb-1">
                {i + 1}. {step.description}
              </div>
              <div className="text-white/50 font-mono text-[10px]">
                {step.formula}
              </div>
              <div className="text-ocean-cyan mt-1">
                {step.result}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface CalculatorProps {
  onResult: (response: CalculationResponse, formattedResult: string) => void;
  onError: (error: string) => void;
  onLoading: (loading: boolean) => void;
}

// Distance Calculator Component
function DistanceCalculator({ onResult, onError, onLoading }: CalculatorProps) {
  const [origin, setOrigin] = useState('Singapore');
  const [destination, setDestination] = useState('Dubai');
  const [speed, setSpeed] = useState('14');
  const [weatherMargin, setWeatherMargin] = useState('5');

  const calculate = async () => {
    onLoading(true);
    try {
      const response = await calculatorApi.calculateDistance({
        origin_port: origin,
        destination_port: destination,
        speed_knots: parseFloat(speed) || 14,
        weather_margin_pct: parseFloat(weatherMargin) || 5,
        departure_datetime: new Date().toISOString()
      });

      const data = response.data;
      const result = data.result as Record<string, string | number>;

      const formattedResult = `
Route: ${result.origin_port} → ${result.destination_port}
Port Codes: ${result.origin_code} → ${result.destination_code}
Route Type: ${(result.route as string)?.charAt(0).toUpperCase() + (result.route as string)?.slice(1) || 'Direct'}

Distance: ${Number(result.distance_nm).toLocaleString()} nautical miles
Vessel Speed: ${result.speed_knots} knots

Base Transit Time:
   • ${result.transit_hours_base} hours
   • ${result.transit_days_base} days

With Weather Margin (${result.weather_margin_pct}%):
   • ${result.transit_hours_with_margin} hours
   • ${result.transit_days_with_margin} days
   • ${result.transit_formatted}

${result.eta ? `ETA: ${new Date(result.eta as string).toLocaleDateString('en-US', {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
})}` : ''}
      `.trim();

      onResult(data, formattedResult);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to calculate distance. Please check port names and try again.';
      onError(errorMessage);
    } finally {
      onLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-white flex items-center gap-2">
        <MapPin className="w-5 h-5 text-ocean-cyan" />
        Distance & ETA Calculator
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-white/60 mb-2">Origin Port</label>
          <input
            type="text"
            value={origin}
            onChange={(e) => setOrigin(e.target.value)}
            placeholder="e.g., Singapore, Rotterdam, Qingdao"
            className={cn(
              'w-full px-4 py-2 rounded-lg',
              'bg-white/5 border border-white/10',
              'text-white placeholder-white/30',
              'focus:outline-none focus:ring-2 focus:ring-ocean-cyan/50'
            )}
          />
        </div>
        <div>
          <label className="block text-sm text-white/60 mb-2">Destination Port</label>
          <input
            type="text"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder="e.g., Dubai, Santos, Newcastle"
            className={cn(
              'w-full px-4 py-2 rounded-lg',
              'bg-white/5 border border-white/10',
              'text-white placeholder-white/30',
              'focus:outline-none focus:ring-2 focus:ring-ocean-cyan/50'
            )}
          />
        </div>
        <div>
          <label className="block text-sm text-white/60 mb-2">Speed (knots)</label>
          <input
            type="number"
            value={speed}
            onChange={(e) => setSpeed(e.target.value)}
            min="1"
            max="30"
            step="0.5"
            className={cn(
              'w-full px-4 py-2 rounded-lg',
              'bg-white/5 border border-white/10',
              'text-white placeholder-white/30',
              'focus:outline-none focus:ring-2 focus:ring-ocean-cyan/50'
            )}
          />
        </div>
        <div>
          <label className="block text-sm text-white/60 mb-2">Weather Margin (%)</label>
          <input
            type="number"
            value={weatherMargin}
            onChange={(e) => setWeatherMargin(e.target.value)}
            min="0"
            max="20"
            step="1"
            className={cn(
              'w-full px-4 py-2 rounded-lg',
              'bg-white/5 border border-white/10',
              'text-white placeholder-white/30',
              'focus:outline-none focus:ring-2 focus:ring-ocean-cyan/50'
            )}
          />
        </div>
      </div>

      <button
        onClick={calculate}
        className={cn(
          'flex items-center gap-2 px-6 py-3 rounded-xl font-medium',
          'bg-gradient-to-r from-ocean-cyan to-ocean-blue',
          'text-white shadow-lg hover:opacity-90 transition-opacity'
        )}
      >
        <Calculator className="w-4 h-4" />
        Calculate
      </button>
    </div>
  );
}

// Laytime Calculator Component
function LaytimeCalculator({ onResult, onError, onLoading }: CalculatorProps) {
  const [cargo, setCargo] = useState('50000');
  const [loadRate, setLoadRate] = useState('5000');
  const [rateType, setRateType] = useState<RateType>('SHINC');
  const [noticeHours, setNoticeHours] = useState('6');

  const rateTypes: { value: RateType; label: string; description: string }[] = [
    { value: 'SHINC', label: 'SHINC', description: 'Sundays & Holidays Included' },
    { value: 'SHEX', label: 'SHEX', description: 'Sundays & Holidays Excepted' },
    { value: 'SHEX_UU', label: 'SHEX UU', description: 'Unless Used' },
    { value: 'SHEX_EIU', label: 'SHEX EIU', description: 'Even If Used' },
    { value: 'WWD', label: 'WWD', description: 'Weather Working Days' },
    { value: 'CQD', label: 'CQD', description: 'Customary Quick Despatch' },
  ];

  const calculate = async () => {
    onLoading(true);
    try {
      const response = await calculatorApi.calculateLaytime({
        cargo_quantity_mt: parseFloat(cargo) || 50000,
        loading_rate_per_day: parseFloat(loadRate) || 5000,
        rate_type: rateType,
        notice_hours: parseFloat(noticeHours) || 6
      });

      const data = response.data;
      const result = data.result as Record<string, string | number>;

      const formattedResult = `
Cargo Quantity: ${Number(result.cargo_quantity_mt).toLocaleString()} MT

Loading Rate: ${Number(result.loading_rate_per_day).toLocaleString()} MT/day
Rate Type: ${result.rate_type}

${result.rate_type_explanation}

Allowed Laytime:
   • ${result.allowed_laytime_hours} hours
   • ${result.allowed_laytime_days} days
   • ${result.laytime_formatted}

Notice Period: ${noticeHours} hours after NOR tendered
${result.laytime_starts ? `Laytime Starts: ${result.laytime_starts}` : ''}
      `.trim();

      onResult(data, formattedResult);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to calculate laytime. Please check inputs and try again.';
      onError(errorMessage);
    } finally {
      onLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-white flex items-center gap-2">
        <Clock className="w-5 h-5 text-ocean-purple" />
        Laytime Calculator
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-white/60 mb-2">Cargo Quantity (MT)</label>
          <input
            type="number"
            value={cargo}
            onChange={(e) => setCargo(e.target.value)}
            min="1"
            className={cn(
              'w-full px-4 py-2 rounded-lg',
              'bg-white/5 border border-white/10',
              'text-white placeholder-white/30',
              'focus:outline-none focus:ring-2 focus:ring-ocean-purple/50'
            )}
          />
        </div>
        <div>
          <label className="block text-sm text-white/60 mb-2">Loading Rate (MT/day)</label>
          <input
            type="number"
            value={loadRate}
            onChange={(e) => setLoadRate(e.target.value)}
            min="1"
            className={cn(
              'w-full px-4 py-2 rounded-lg',
              'bg-white/5 border border-white/10',
              'text-white placeholder-white/30',
              'focus:outline-none focus:ring-2 focus:ring-ocean-purple/50'
            )}
          />
        </div>
        <div>
          <label className="block text-sm text-white/60 mb-2">Rate Type</label>
          <select
            value={rateType}
            onChange={(e) => setRateType(e.target.value as RateType)}
            className={cn(
              'w-full px-4 py-2 rounded-lg',
              'bg-white/5 border border-white/10',
              'text-white',
              'focus:outline-none focus:ring-2 focus:ring-ocean-purple/50'
            )}
          >
            {rateTypes.map(rt => (
              <option key={rt.value} value={rt.value} className="bg-slate-800">
                {rt.label} - {rt.description}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm text-white/60 mb-2">Notice Hours (after NOR)</label>
          <input
            type="number"
            value={noticeHours}
            onChange={(e) => setNoticeHours(e.target.value)}
            min="0"
            max="72"
            className={cn(
              'w-full px-4 py-2 rounded-lg',
              'bg-white/5 border border-white/10',
              'text-white placeholder-white/30',
              'focus:outline-none focus:ring-2 focus:ring-ocean-purple/50'
            )}
          />
        </div>
      </div>

      <button
        onClick={calculate}
        className={cn(
          'flex items-center gap-2 px-6 py-3 rounded-xl font-medium',
          'bg-gradient-to-r from-ocean-purple to-ocean-pink',
          'text-white shadow-lg hover:opacity-90 transition-opacity'
        )}
      >
        <Calculator className="w-4 h-4" />
        Calculate
      </button>
    </div>
  );
}

// Demurrage Calculator Component
function DemurrageCalculator({ onResult, onError, onLoading }: CalculatorProps) {
  const [allowedLaytime, setAllowedLaytime] = useState('10');
  const [actualTime, setActualTime] = useState('12.5');
  const [demurrageRate, setDemurrageRate] = useState('25000');
  const [despatchRate, setDespatchRate] = useState('');

  const calculate = async () => {
    onLoading(true);
    try {
      const despatchRateNum = despatchRate ? parseFloat(despatchRate) : undefined;

      const response = await calculatorApi.calculateDemurrage({
        allowed_laytime_days: parseFloat(allowedLaytime) || 10,
        actual_time_days: parseFloat(actualTime) || 12.5,
        demurrage_rate_per_day: parseFloat(demurrageRate) || 25000,
        despatch_rate_per_day: despatchRateNum,
        despatch_type: 'DATS'
      });

      const data = response.data;
      const result = data.result as Record<string, string | number>;

      const isDemurrage = result.status === 'DEMURRAGE';

      const formattedResult = `
Allowed Laytime: ${allowedLaytime} days
Actual Time Used: ${actualTime} days

${isDemurrage ? `
DEMURRAGE SITUATION

Time on Demurrage: ${result.time_on_demurrage_days} days (${result.time_on_demurrage_formatted})
Demurrage Rate: $${parseFloat(demurrageRate).toLocaleString()}/day

DEMURRAGE DUE: $${Number(result.demurrage_amount_usd).toLocaleString()}

${result.net_description}
` : `
DESPATCH SITUATION

Time Saved: ${result.time_saved_days} days (${result.time_saved_formatted})
Despatch Type: ${result.despatch_type || 'DATS'} (Despatch All Time Saved)

DESPATCH EARNED: $${Number(result.despatch_amount_usd).toLocaleString()}

${result.net_description}
`}
      `.trim();

      onResult(data, formattedResult);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to calculate demurrage. Please check inputs and try again.';
      onError(errorMessage);
    } finally {
      onLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-white flex items-center gap-2">
        <DollarSign className="w-5 h-5 text-ocean-pink" />
        Demurrage Calculator
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-white/60 mb-2">Allowed Laytime (days)</label>
          <input
            type="number"
            step="0.5"
            value={allowedLaytime}
            onChange={(e) => setAllowedLaytime(e.target.value)}
            min="0"
            className={cn(
              'w-full px-4 py-2 rounded-lg',
              'bg-white/5 border border-white/10',
              'text-white placeholder-white/30',
              'focus:outline-none focus:ring-2 focus:ring-ocean-pink/50'
            )}
          />
        </div>
        <div>
          <label className="block text-sm text-white/60 mb-2">Actual Time (days)</label>
          <input
            type="number"
            step="0.5"
            value={actualTime}
            onChange={(e) => setActualTime(e.target.value)}
            min="0"
            className={cn(
              'w-full px-4 py-2 rounded-lg',
              'bg-white/5 border border-white/10',
              'text-white placeholder-white/30',
              'focus:outline-none focus:ring-2 focus:ring-ocean-pink/50'
            )}
          />
        </div>
        <div>
          <label className="block text-sm text-white/60 mb-2">Demurrage Rate ($/day)</label>
          <input
            type="number"
            value={demurrageRate}
            onChange={(e) => setDemurrageRate(e.target.value)}
            min="0"
            className={cn(
              'w-full px-4 py-2 rounded-lg',
              'bg-white/5 border border-white/10',
              'text-white placeholder-white/30',
              'focus:outline-none focus:ring-2 focus:ring-ocean-pink/50'
            )}
          />
        </div>
        <div>
          <label className="block text-sm text-white/60 mb-2">Despatch Rate ($/day) - Optional</label>
          <input
            type="number"
            value={despatchRate}
            onChange={(e) => setDespatchRate(e.target.value)}
            min="0"
            placeholder="Default: 50% of demurrage"
            className={cn(
              'w-full px-4 py-2 rounded-lg',
              'bg-white/5 border border-white/10',
              'text-white placeholder-white/30',
              'focus:outline-none focus:ring-2 focus:ring-ocean-pink/50'
            )}
          />
        </div>
      </div>

      <button
        onClick={calculate}
        className={cn(
          'flex items-center gap-2 px-6 py-3 rounded-xl font-medium',
          'bg-gradient-to-r from-ocean-pink to-signal-error',
          'text-white shadow-lg hover:opacity-90 transition-opacity'
        )}
      >
        <Calculator className="w-4 h-4" />
        Calculate
      </button>
    </div>
  );
}

// Freight Calculator Component
function FreightCalculator({ onResult, onError, onLoading }: CalculatorProps) {
  const [cargo, setCargo] = useState('50000');
  const [freightRate, setFreightRate] = useState('15');
  const [addressCommission, setAddressCommission] = useState('3.75');
  const [brokerage, setBrokerage] = useState('1.25');

  const calculate = async () => {
    onLoading(true);
    try {
      const response = await calculatorApi.calculateFreight({
        cargo_quantity_mt: parseFloat(cargo) || 50000,
        freight_rate_per_mt: parseFloat(freightRate) || 15,
        address_commission_pct: parseFloat(addressCommission) || 3.75,
        brokerage_commission_pct: parseFloat(brokerage) || 1.25
      });

      const data = response.data;
      const result = data.result as Record<string, string | number | unknown[]>;

      // Build commission breakdown
      let commissionBreakdown = '';
      if (result.commission_breakdown && Array.isArray(result.commission_breakdown)) {
        commissionBreakdown = (result.commission_breakdown as Array<{ recipient: string; percentage: string; amount_usd: string }>)
          .map(c => `   ${c.recipient}: ${c.percentage}% = $${Number(c.amount_usd).toLocaleString()}`)
          .join('\n');
      }

      const formattedResult = `
Cargo: ${parseFloat(cargo).toLocaleString()} MT
Freight Rate: $${freightRate}/MT

FREIGHT CALCULATION:

Gross Freight:        $${Number(result.gross_freight_usd).toLocaleString()}

Commissions (${result.total_commission_pct}%):
${commissionBreakdown}
-----------------------------------
Total Commission:     $${Number(result.total_commission_usd).toLocaleString()}

NET TO OWNER:         $${Number(result.net_to_owner_usd).toLocaleString()}
      `.trim();

      onResult(data, formattedResult);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to calculate freight. Please check inputs and try again.';
      onError(errorMessage);
    } finally {
      onLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-white flex items-center gap-2">
        <Ship className="w-5 h-5 text-ocean-blue" />
        Freight & Commission Calculator
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-white/60 mb-2">Cargo Quantity (MT)</label>
          <input
            type="number"
            value={cargo}
            onChange={(e) => setCargo(e.target.value)}
            min="1"
            className={cn(
              'w-full px-4 py-2 rounded-lg',
              'bg-white/5 border border-white/10',
              'text-white placeholder-white/30',
              'focus:outline-none focus:ring-2 focus:ring-ocean-blue/50'
            )}
          />
        </div>
        <div>
          <label className="block text-sm text-white/60 mb-2">Freight Rate ($/MT)</label>
          <input
            type="number"
            value={freightRate}
            onChange={(e) => setFreightRate(e.target.value)}
            min="0"
            step="0.5"
            className={cn(
              'w-full px-4 py-2 rounded-lg',
              'bg-white/5 border border-white/10',
              'text-white placeholder-white/30',
              'focus:outline-none focus:ring-2 focus:ring-ocean-blue/50'
            )}
          />
        </div>
        <div>
          <label className="block text-sm text-white/60 mb-2">Address Commission (%)</label>
          <input
            type="number"
            value={addressCommission}
            onChange={(e) => setAddressCommission(e.target.value)}
            min="0"
            max="10"
            step="0.25"
            className={cn(
              'w-full px-4 py-2 rounded-lg',
              'bg-white/5 border border-white/10',
              'text-white placeholder-white/30',
              'focus:outline-none focus:ring-2 focus:ring-ocean-blue/50'
            )}
          />
        </div>
        <div>
          <label className="block text-sm text-white/60 mb-2">Brokerage (%)</label>
          <input
            type="number"
            value={brokerage}
            onChange={(e) => setBrokerage(e.target.value)}
            min="0"
            max="5"
            step="0.25"
            className={cn(
              'w-full px-4 py-2 rounded-lg',
              'bg-white/5 border border-white/10',
              'text-white placeholder-white/30',
              'focus:outline-none focus:ring-2 focus:ring-ocean-blue/50'
            )}
          />
        </div>
      </div>

      <button
        onClick={calculate}
        className={cn(
          'flex items-center gap-2 px-6 py-3 rounded-xl font-medium',
          'bg-gradient-to-r from-ocean-blue to-ocean-cyan',
          'text-white shadow-lg hover:opacity-90 transition-opacity'
        )}
      >
        <Calculator className="w-4 h-4" />
        Calculate
      </button>
    </div>
  );
}

export default CalculatorTab;
